import {
  withAuthenticationContext,
  useAuthenticationContext,
  useSecureGraphqlQuery,
  TFunction,
} from '@psyomics/ui-common';
import {
  ActivityStat,
  Button,
  Disclaimer,
  DisclaimerVariant,
  Header,
  LoadingSpinner,
  type ActivityStatIcon,
} from '@psyomics/ui-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  GetOrgStatsDocument,
  GetOrgStatsQuery,
  GetOrgStatsQueryVariables,
  GetOrgStatsResponse,
} from '../features/referrals/generated/referrals';
import { Referrals } from '../features/referrals/referrals';

const COUNT_DAYS = 30;
const MILLISECONDS_PER_DAY = 86400000;

const buildStats = (
  t: TFunction,
  stats: GetOrgStatsResponse,
  organisation: string
): Array<{
  icon: ActivityStatIcon;
  subtext: string;
  title: string;
  value: string;
  href?: string;
}> => [
  {
    href: `/account/${organisation}/home/activity-dashboard`,
    icon: 'send',
    subtext: t('page.clinicianHomepage.latestActivity.invitesCard.subtext', {
      days: COUNT_DAYS,
    }),
    title: t('page.clinicianHomepage.latestActivity.invitesCard.title'),
    value: String(stats.inviteCount),
  },
  {
    href: `/account/${organisation}/home/activity-dashboard`,
    icon: 'checkCircle2',
    subtext: t('page.clinicianHomepage.latestActivity.reportsCompletedCard.subtext', { days: COUNT_DAYS }),
    title: t('page.clinicianHomepage.latestActivity.reportsCompletedCard.title'),
    value: String(stats.reportsCompletedCount),
  },
  {
    href: `/account/${organisation}/home/activity-dashboard`,
    icon: 'lineChart',
    subtext: t('page.clinicianHomepage.latestActivity.completionRateCard.subtext', { days: COUNT_DAYS }),
    title: t('page.clinicianHomepage.latestActivity.completionRateCard.title'),
    value: stats.completionRatePercentage == null ? '- %' : `${stats.completionRatePercentage}%`,
  },
];

const Stats = () => {
  const { t } = useTranslation();
  const { organisation } = useAuthenticationContext();
  const navigate = useNavigate();

  const fromDate = String(new Date(Date.now() - (COUNT_DAYS - 1) * MILLISECONDS_PER_DAY));
  const toDate = String(new Date());

  const [{ data, fetching }] = useSecureGraphqlQuery<GetOrgStatsQuery, GetOrgStatsQueryVariables>({
    query: GetOrgStatsDocument,
    variables: {
      input: {
        fromDate,
        toDate,
      },
    },
  });

  const stats = useMemo(
    () => (data?.getOrgStats ? buildStats(t, data.getOrgStats, organisation) : []),
    [t, data?.getOrgStats, organisation]
  );

  if (fetching) return <LoadingSpinner />;

  return (
    <>
      <div className="flex items-center gap-4 pb-2">
        <Header className="text-slate-800 text-2xl font-semibold" level="h2">
          {t('page.clinicianHomepage.latestActivity.title')}
        </Header>
        <Button
          appearance="secondary"
          onClick={() => navigate(`/account/${organisation}/home/activity-dashboard`)}
          size="sm"
        >
          {t('page.clinicianHomepage.latestActivity.buttonText')}
        </Button>
      </div>
      <div className="flex flex-wrap gap-[19px] w-full">
        {stats.map(({ icon, subtext, title, value, href }) => (
          <div className="flex-1 basis-[200px] max-w-[300px]" key={title}>
            <ActivityStat href={href} icon={icon} subtext={subtext} title={title} value={value} />
          </div>
        ))}
      </div>
    </>
  );
};

const ReferralsList = () => {
  const { t } = useTranslation();
  const { organisation } = useAuthenticationContext();
  const navigate = useNavigate();

  return (
    <div className="mt-[30px]">
      <div className="flex items-center gap-4 pb-2">
        <Header className="text-slate-800 text-2xl font-semibold" level="h2">
          {t('page.clinicianHomepage.latestCompletions.title')}
        </Header>

        <Button appearance="secondary" onClick={() => navigate(`/account/${organisation}/home/referrals`)} size="sm">
          {t('page.clinicianHomepage.latestCompletions.buttonText')}
        </Button>
      </div>
      <Referrals isSummary />
    </div>
  );
};

const InnerHome = () => {
  const { t } = useTranslation();

  return (
    <div className="bg-gray-50 min-h-screen pt-8">
      <div className="container max-w-5xl mx-auto px-4">
        <Disclaimer showIcon={true} variant={DisclaimerVariant.WARNING}>
          {t('page.clinicianHomepage.disclaimer.text')}
        </Disclaimer>
        <Header className="mb-[22px] mt-[22px]" level="h1">
          {t('page.clinicianHomepage.title')}
        </Header>
        <Stats />
        <ReferralsList />
      </div>
    </div>
  );
};

const Home = withAuthenticationContext(InnerHome);

export { Home };
